/**
 * Esc and a click outside close the summoned chat window (Part 09 fix b).
 *
 * Esc closes one layer: a key the chat's own fields, menus or pockets
 * handled (`defaultPrevented`), an open menu or dialog, or IME composition
 * leave it open. Keys from inside the window arrive through the window's
 * React handler, after its composer has seen them; with nothing focused, a
 * capture listener takes the key before the wall and the stop-agent
 * shortcut do. A key from another focused field belongs to that field.
 *
 * Over the Workspace a transparent backdrop under the window closes it on a
 * press and release on the backdrop itself, as the Focus backdrop does; a
 * press inside the window that ends outside never closes it.
 */
import {
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

const OPEN_LAYER =
  '[role="menu"], [role="dialog"], [role="alertdialog"], [role="listbox"]';

function closesOnEscape(event: KeyboardEvent): boolean {
  return (
    event.key === "Escape" &&
    !event.defaultPrevented &&
    !event.isComposing &&
    document.querySelector(OPEN_LAYER) === null
  );
}

function nothingFocused(target: EventTarget | null): boolean {
  return (
    target === null ||
    target === document.body ||
    target === document.documentElement
  );
}

export function useWorkspaceConversationDismiss({
  open,
  surfaceRef,
  close,
}: {
  open: boolean;
  surfaceRef: RefObject<HTMLElement | null>;
  close: () => void;
}) {
  const backdropPressRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!nothingFocused(event.target) || !closesOnEscape(event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [close, open]);

  return {
    surfaceProps: {
      onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
        if (
          !(event.target instanceof Node) ||
          !surfaceRef.current?.contains(event.target) ||
          !closesOnEscape(event.nativeEvent)
        )
          return;
        event.preventDefault();
        // The wall's selection and the stop-agent shortcut listen on document.
        event.nativeEvent.stopImmediatePropagation();
        close();
      },
      onPointerDownCapture: () => {
        backdropPressRef.current = false;
      },
    },
    backdropProps: {
      onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
        backdropPressRef.current = event.target === event.currentTarget;
      },
      onPointerUp: (event: ReactPointerEvent<HTMLElement>) => {
        const pressed = backdropPressRef.current;
        backdropPressRef.current = false;
        if (pressed && event.target === event.currentTarget) close();
      },
    },
  };
}
